import { Banner, Heading } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { FBCtmAdsSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useAdAccountCRUDActions } from '@mm/state/actions/AdAccountCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { CtmAdModel } from '@mm/state/model/CtmAdModel';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { useAutomation } from '../../../../features/beta/modules/Automations/AutomationEdit/use-automation';
import { CtmAdDetail } from '../../../../features/clickToMessengerAds/components/CtmAdDetail/CtmAdDetail';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './CTMAdSlideoutEditor.module.css';

export interface CTMAdsSlideoutEditorProps {
  ad: CtmAdModel;
  skipAdAccountsRetrieve?: boolean;
  campaignId: number;
}

export const CTMAdSlideoutEditor = ({
  ad,
  skipAdAccountsRetrieve,
  campaignId,
}: CTMAdsSlideoutEditorProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const fbPageId = useSelector(activeFacebookPageIdSelector);
  const viewActions = useViewActions();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;
  const adAccountCRUDActions = useAdAccountCRUDActions();
  const { actions } = useAutomation({ id: campaignId });
  const { setRef } = useSlideoutOnClose();

  const [localAd, setLocalAd] = useState(ad);

  useEffect(() => {
    if (!skipAdAccountsRetrieve) {
      adAccountCRUDActions.retrieveAll(fbPageId, false, '');
    }
    return () => {
      if (campaignId) {
        actions.fetchAutomation();
      }
    };
  }, []);

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <SlideoutMenu.Header className={styles.header} closeIcon="close">
        <Heading size={3} className={styles.heading}>
          Facebook Click-to-Messenger Ads
        </Heading>
      </SlideoutMenu.Header>
      <FreeTrialWarning />

      {!isMobile && (
        <Banner
          css={{
            container: styles.bannerContainer,
            header: styles.bannerHeader,
            collapsedHeader: styles.collapsedHeader,
            content: styles.bannerContent,
          }}
          withBannerHeader
          headerText="What is Facebook Click-to-Messenger Ads?"
          withLeftArea={false}
          showCloseIcon
          usedInSlideout
        >
          <FBCtmAdsSlideoutBanner />
        </Banner>
      )}
      <SlideoutMenu.Content className={styles.content}>
        <div className={classnames(styles.configArea)}>
          <CtmAdDetail
            ctmAdOverride={localAd}
            setCtmAdOverrride={setLocalAd}
            botIdOverride={chatbotId}
            usedInSlideout
            onActivateSuccess={() => viewActions.hideSlideoutMenu()}
          />
        </div>
        {!isMobile && localAd?.ad?.pageId && (
          <DialoguePreview
            dialogueId={localAd?.ad.pageId}
            className={styles.dialoguePreview}
            sticky
          />
        )}
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
